/**
 * Draggable floating window.
 *
 * Panels overlap the world rather than living in a fixed rail, so several can be
 * open and arranged at once — reading the market while inspecting a parcel.
 * Dragging is pointer-based so it works with mouse, pen and touch, and the
 * window is clamped to stay on screen.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { JSX } from "react";

export interface WindowProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
  /** Initial position, in px from the top-left of the viewport. */
  initial: { x: number; y: number };
  width: number;
  /** Max height; the body scrolls beyond it. */
  maxHeight?: number;
  /** Raised above its siblings when true. */
  focused?: boolean;
  onFocus?: () => void;
  /** Optional tab strip rendered between the title bar and the body. */
  tabs?: ReactNode;
}

const MARGIN = 8;

export function Window({
  title,
  children,
  onClose,
  initial,
  width,
  maxHeight = 560,
  focused = false,
  onFocus,
  tabs,
}: WindowProps): JSX.Element {
  const [pos, setPos] = useState(initial);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const shell = useRef<HTMLDivElement>(null);

  const clamp = useCallback(
    (x: number, y: number) => {
      const w = shell.current?.offsetWidth ?? width;
      const h = shell.current?.offsetHeight ?? 200;
      return {
        // Keep at least a sliver on screen in every direction so a window can
        // never be dragged somewhere it cannot be grabbed again.
        x: Math.min(Math.max(MARGIN, x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
        y: Math.min(Math.max(MARGIN, y), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
      };
    },
    [width],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Ignore drags started on the close button.
    if ((e.target as HTMLElement).closest("button")) return;
    onFocus?.();
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!drag.current) return;
    setPos(clamp(e.clientX - drag.current.dx, e.clientY - drag.current.dy));
  };

  const endDrag = (): void => {
    drag.current = null;
  };

  // Nudge back into view if the viewport shrinks under an open window.
  useEffect(() => {
    const onResize = (): void => setPos((p) => clamp(p.x, p.y));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <section
      ref={shell}
      className={`window${focused ? " focused" : ""}`}
      style={{ left: pos.x, top: pos.y, width, maxHeight, zIndex: focused ? 55 : 50 }}
      onPointerDown={() => onFocus?.()}
      aria-label={title}
    >
      <header
        className="window-bar"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span className="window-title">{title}</span>
        <button className="window-close" onClick={onClose} aria-label={`Close ${title}`}>
          ×
        </button>
      </header>
      {tabs}
      <div className="window-body">{children}</div>
    </section>
  );
}
